
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTasks } from '@/hooks/useTaskQueries';
import { Task } from '@/types/task';
import { useAuth } from '@/contexts/AuthContext';
import { TasksHeader } from '@/components/Tasks/TasksHeader';
import { DialogManager } from '@/components/Tasks/DialogManager';
import { TaskActions } from '@/components/Tasks/TaskActions';
import { DailyQuoteProvider } from '@/components/Tasks/DailyQuoteProvider';
import { TasksSectionContent } from '@/components/Tasks/TasksSectionContent';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTaskDialogs } from '@/hooks/useTaskDialogs';
import { useTaskActions } from '@/hooks/useTaskActions';
import { useWeeklyResetMutation } from '@/hooks/mutations/useWeeklyResetMutation';

export const TasksSectionWrapper = () => {
  const navigate = useNavigate();
  const [dailyQuote, setDailyQuote] = useState('Keep track of tasks with your feline friend!');
  
  const { user, profile } = useAuth();
  const isAdmin = profile?.role === 'admin' || false;
  const isMobile = useIsMobile();

  const { data: tasks = [], isLoading, refetch: refetchTasks } = useTasks();
  const weeklyResetMutation = useWeeklyResetMutation();
  
  const {
    taskLogOpen,
    setTaskLogOpen,
    newTaskDialogOpen,
    setNewTaskDialogOpen,
    templateDialogOpen,
    setTemplateDialogOpen,
    emailLogsOpen,
    setEmailLogsOpen,
    userManagementOpen,
    setUserManagementOpen,
    isClearDialogOpen,
    setIsClearDialogOpen,
  } = useTaskDialogs();

  const {
    handleAddTask,
    handleCompleteTask,
    handleDeleteTask,
    handleEditTask,
    handleReactivateTask,
    handleExportTasks,
    handleImportTasks,
    handleClearDatabase,
  } = useTaskActions(refetchTasks, isAdmin);

  const onAddTask = (data: any) => {
    handleAddTask(data);
    setNewTaskDialogOpen(false);
  };

  const onClearDatabase = async () => {
    const success = await handleClearDatabase();
    if (success) {
      setIsClearDialogOpen(false);
    }
  };

  const handleWeeklyReset = async () => {
    return await weeklyResetMutation.mutateAsync();
  };

  return (
    <>
      <DailyQuoteProvider onQuoteLoaded={setDailyQuote} />
      <TasksHeader dailyQuote={dailyQuote} />

      <DialogManager
        taskLogOpen={taskLogOpen}
        onTaskLogOpenChange={setTaskLogOpen}
        templateDialogOpen={templateDialogOpen}
        onTemplateDialogOpenChange={setTemplateDialogOpen}
        emailLogsOpen={emailLogsOpen}
        onEmailLogsOpenChange={setEmailLogsOpen}
        newTaskDialogOpen={newTaskDialogOpen}
        onNewTaskDialogOpenChange={setNewTaskDialogOpen}
        userManagementOpen={userManagementOpen}
        onUserManagementOpenChange={setUserManagementOpen}
        isClearDialogOpen={isClearDialogOpen}
        onClearDialogOpenChange={setIsClearDialogOpen}
        onAddTask={onAddTask}
        onClearDatabase={onClearDatabase}
      />

      <TasksSectionContent
        tasks={tasks}
        isLoading={isLoading}
        isAdmin={isAdmin}
        onComplete={handleCompleteTask}
        onDelete={handleDeleteTask}
        onEdit={handleEditTask}
        onReactivate={handleReactivateTask}
      />

      <TaskActions
        isAdmin={isAdmin}
        isMobile={isMobile}
        onOpenDialog={() => setNewTaskDialogOpen(true)}
        onOpenLog={() => setTaskLogOpen(true)}
        onExport={() => handleExportTasks(tasks)}
        onImport={handleImportTasks}
        onClearDatabase={() => setIsClearDialogOpen(true)}
        onManageTemplates={() => setTemplateDialogOpen(true)}
        onShowEmailLogs={() => setEmailLogsOpen(true)}
        onManageUsers={() => setUserManagementOpen(true)}
        onWeeklyCalendar={() => navigate('/weekly-calendar')}
        onWeeklyReset={isAdmin ? handleWeeklyReset : undefined}
      />
    </>
  );
};
